import React from "react";
import autobind from "class-autobind";
import { FaChevronRight, FaChevronDown } from "react-icons/fa";
import { Checkbox } from "../checkbox/index";
import difference from "lodash/difference";
import pullAll from "lodash/pullAll";

interface TreeNode {
  value: string;
  label: string;
  children?: TreeNode[] | null;
}
interface Props {
  id: string;
  nodes: TreeNode[];
  selected: string[];
  onChange: (selectedValues: string[]) => void;
}
interface State {
  active: string[];
}

export class FormTreeSelect extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    autobind(this);
    this.state = {
      active: [],
    };
  }

  isActive = (key: string) => {
    const existing = this.state.active.indexOf(key);
    return existing !== -1;
  }

  toggleActive = (key: string) => {
    const active = [ ...this.state.active ];
    const existing = active.indexOf(key);
    if (existing !== -1) {
      active.splice(existing, 1);
    }
    else {
      active.push(key);
    }
    this.setState({ active });
  }

  toggleSelectValue(selected: string[], value: string) {
    const existing = selected.indexOf(value);
    if (existing === -1)
      selected.push(value);
    else
      selected.splice(existing, 1);
  }

  nodeGetDeepestChildrenValues(node: TreeNode): string[] {
    let children: string[] = [];
    if (node.children) {
      for (const child of node.children) {
        if (child.children) {
          children = [ ...children, ...this.nodeGetDeepestChildrenValues(child) ];
        }
        else {
          children.push(child.value);
        }
      }
    }
    return children;
  }

  onSelect(node: TreeNode) {
    const { onChange } = this.props;
    let selected = [...this.props.selected];
    if (node.children) {
      const childValues = this.nodeGetDeepestChildrenValues(node);
      const unselectedChildren = difference(childValues, selected);
      if (unselectedChildren.length > 0) { // SOME OR ALL UNSELECTED | SELECT ALL
        unselectedChildren.forEach((v) => {
          if (selected.indexOf(v) === -1)
            selected.push(v);
        });
      }
      else if (unselectedChildren.length === 0) { // ALL SELECTED | REMOVE ALL
        selected = pullAll(selected, childValues);
      }
    }
    else {
      this.toggleSelectValue(selected, node.value);
    }
    onChange(selected);
  }

  renderNodes(nodes: TreeNode[], depth: number, parentIndex: number) {
    const { id, selected } = this.props;
    return (
      <div className={depth === 0 ? "" : "m-l-2"}>
        {nodes.map((node, i) => {
          const key = `${depth}-${parentIndex}-${i}`;
          const hasChildren = !!node.children;
          const isActive = hasChildren ? this.isActive(key) : false;

          if (depth === 0 && (!node.children || (node.children && node.children.length === 0)))
            return null;

          let checkedPartial = false;
          let checked = false;

          if (!hasChildren) {
            checked = selected.indexOf(node.value) !== -1;
          }
          else {
            const childValues = this.nodeGetDeepestChildrenValues(node);
            if (childValues.length > 0) {
              const unselectedChildren = difference(childValues, selected);
              if (unselectedChildren.length === childValues.length) { // NOTHING SELECTED
                // checked = true;
              }
              else if (unselectedChildren.length > 0) { // SOME SELECTED
                checkedPartial = true;
              }
              else if (unselectedChildren.length === 0) { // ALL SELECTED
                checked = true;
              }
            }
          }

          return (
            <div key={i} className={(depth === 0 && i === 0) ? "" : "p-t-2"}>
              <div className="flex-line">
                {hasChildren && (
                  <div className="cursor flex-center" style={{width: "30px"}} onClick={() => this.toggleActive(key)}>
                    {isActive ? <FaChevronDown/> : <FaChevronRight/>}
                  </div>
                )}
                {!hasChildren && (
                  <div style={{width: "30px"}}/>
                )}
                <Checkbox
                  id={`tree-${id}-${key}`}
                  name={`tree-${id}-${key}`}
                  checked={checked}
                  checkedPartial={checkedPartial}
                  onChange={() => this.onSelect(node)}
                  label={node.label}/>
              </div>
              {(isActive && node.children) && this.renderNodes(node.children, depth + 1, i)}
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    const { nodes } = this.props;
    return this.renderNodes(nodes, 0, 0);
  }

}
